import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  GridLegacy as Grid,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Tooltip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import { AppButton, AppIconButton, AppTextField } from '../../components/design-system';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  LocalShipping as ShippingIcon,
  ContentCopy as CopyIcon,
  Print as PrintIcon,
  CheckCircleOutline as ConfirmIcon,
  DoDisturbAlt as StopIcon,
  Send as ShipIcon,
  AddCircleOutline as AddDevicesIcon,
  AssignmentReturn as ReturnIcon,
  HowToReg as ClaimOnBehalfIcon,
} from '@mui/icons-material';
import { EnhancedTable, type TableColumn } from '../../components/ui/EnhancedTable';
import { providerOrdersService } from '../../services/ProviderOrdersService';
import { opsDevicesService } from '../../services/OpsDevicesService';
import ReturnDeviceModal from './components/ReturnDeviceModal';
import ProviderClaimOnBehalfModal from './components/ProviderClaimOnBehalfModal';
import { useAuth } from '../../context/AppContext';
import { PROVIDER_ADMIN_DEPLOYMENT_ROLES } from '@cnapse/shared';
import type {
  Order,
  OpsDeviceDetail,
  OrderStatus,
  UpdateOrderRequest,
  OrderInventoryAvailability,
  DeviceInventoryStatus,
} from '@cnapse/shared/schemas.js';
import type { OpsDeviceCategory } from '@cnapse/shared/schemas.js';
import {
  getDeviceCategoryFilterLabel,
  getDeviceCategoryLabel,
  formatOrderLineItemSummary,
} from '@cnapse/shared';

import {
  ORDER_STATUS_OPTIONS,
  OrderStatusChip,
} from './orderStatusDisplay';
import {
  DeviceInventoryStatusChip,
  getDeviceInventoryStatusLabel,
} from './deviceInventoryStatusDisplay';

const getCategoryLabel = (category: string | null | undefined): string =>
  getDeviceCategoryLabel(category);

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ─── DetailRow — matches DeviceDetail.tsx exactly ────────────────────────────

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box display="flex" alignItems="baseline" gap={1} py={0.75}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontFamily="monospace" component="div">
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

const RETURNABLE_DEVICE_STATUSES: DeviceInventoryStatus[] = ['shipped', 'claimed', 'active'];

function isReturnableDeviceStatus(status: string | null | undefined): boolean {
  return RETURNABLE_DEVICE_STATUSES.includes(status as DeviceInventoryStatus);
}

// ─── Device fleet columns ─────────────────────────────────────────────────────

function buildDeviceColumns(
  navigateTo: (path: string) => void,
  options?: { onReturn?: (device: OpsDeviceDetail) => void },
): TableColumn<OpsDeviceDetail>[] {
  const columns: TableColumn<OpsDeviceDetail>[] = [
    {
      field: 'serialNumber',
      headerName: 'Serial Number',
      minWidth: 200,
      renderCell: (row) => (
        <Typography
          variant="body2"
          fontFamily="monospace"
          fontWeight="bold"
          color="primary.main"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigateTo(`/provider/devices/${row.serialNumber}`)}
        >
          {row.serialNumber}
        </Typography>
      ),
    },
    {
      field: 'deviceCategory',
      headerName: 'Device Type',
      minWidth: 150,
      renderCell: (row) => (
        <Typography variant="body2">
          {getCategoryLabel(row.deviceCategory)}
        </Typography>
      ),
    },
    {
      field: 'deviceModel',
      headerName: 'Model',
      minWidth: 100,
      renderCell: (row) => <Typography variant="body2">{row.deviceModel}</Typography>,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 140,
      renderCell: (row) => {
        const status = row.inventoryStatus ?? 'in-stock';
        return <DeviceInventoryStatusChip status={status} />;
      },
    },
  ];

  if (options?.onReturn) {
    columns.push({
      field: 'returnAction',
      headerName: 'Return',
      minWidth: 110,
      renderCell: (row) => {
        const status = row.inventoryStatus ?? 'in-stock';
        if (!isReturnableDeviceStatus(status)) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        return (
          <AppButton
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<ReturnIcon />}
            onClick={(e) => {
              e.stopPropagation();
              options.onReturn!(row);
            }}
          >
            Return
          </AppButton>
        );
      },
    });
  }

  return columns;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

// ─── Add Devices Dialog ───────────────────────────────────────────────────────

const CATEGORY_OPTIONS: Array<{ value: OpsDeviceCategory; label: string }> = [
  { value: 'efem-gateway', label: getDeviceCategoryFilterLabel('efem-gateway') },
  { value: 'location-tag', label: getDeviceCategoryFilterLabel('location-tag') },
  { value: 'location-anchor', label: getDeviceCategoryFilterLabel('location-anchor') },
  { value: 'barcode-scanner', label: getDeviceCategoryFilterLabel('barcode-scanner') },
  { value: 'handheld-barcode-scanner', label: getDeviceCategoryFilterLabel('handheld-barcode-scanner') },
  { value: 'waferid-camera', label: getDeviceCategoryFilterLabel('waferid-camera') },
  { value: 'location-gateway', label: getDeviceCategoryFilterLabel('location-gateway') },
];

interface AddDevicesDialogProps {
  open: boolean;
  order: Order;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

function AddDevicesDialog({ open, order, onClose, onSuccess }: AddDevicesDialogProps) {
  const [allInStock, setAllInStock] = useState<OpsDeviceDetail[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // EnhancedTable search/filter/page state (client-side)
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);


  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  const [allocating, setAllocating] = useState(false);
  const [allocateProgress, setAllocateProgress] = useState(0);
  const [allocateError, setAllocateError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoadingDevices(true);
    setLoadError(null);
    setSelectedIds([]);
    setAllocateError(null);
    setPage(1);
    void opsDevicesService
      .listDevices({ inventoryStatus: 'in-stock', limit: 200 })
      .then((res) => setAllInStock(res.data.devices))
      .catch((e) => setLoadError(e instanceof Error ? e.message : 'Failed to load devices'))
      .finally(() => setLoadingDevices(false));
  }, [open, order.tenantId]);

  // Client-side filter on top of in-stock list
  const filtered = useMemo(() => {
    let list = allInStock;
    if (filters['deviceCategory']) {
      list = list.filter((d) => d.deviceCategory === filters['deviceCategory']);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (d) =>
          d.serialNumber.toLowerCase().includes(q) ||
          (d.deviceModel ?? '').toLowerCase().includes(q) ||
          (d.mfgBatch ?? '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [allInStock, filters, searchTerm]);

  // Paginate client-side
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const tableColumns: TableColumn<OpsDeviceDetail>[] = [
    {
      field: 'serialNumber',
      headerName: 'Serial Number',
      minWidth: 200,
      renderCell: (row) => (
        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
          {row.serialNumber}
        </Typography>
      ),
    },
    {
      field: 'deviceCategory',
      headerName: 'Type',
      minWidth: 150,
      renderCell: (row) => (
        <Typography variant="body2">{getCategoryLabel(row.deviceCategory)}</Typography>
      ),
    },
    {
      field: 'deviceModel',
      headerName: 'Model',
      minWidth: 100,
      renderCell: (row) => <Typography variant="body2">{row.deviceModel ?? '—'}</Typography>,
    },
    {
      field: 'mfgBatch',
      headerName: 'Batch',
      minWidth: 140,
      renderCell: (row) => (
        <Typography variant="body2" fontFamily="monospace" color="text.secondary">
          {row.mfgBatch ?? '—'}
        </Typography>
      ),
    },
  ];

  const handleAllocate = async () => {
    if (selectedIds.length === 0) return;

    setAllocating(true);
    setAllocateError(null);
    setAllocateProgress(0);

    const sns = selectedIds as string[];
    const results = await Promise.allSettled(
      sns.map((sn) =>
        opsDevicesService.allocateDevice(sn, {
          tenantId: order.tenantId as import('@cnapse/shared/data.types.js').Uuid,                    
          orderId: order.orderReference,
        }),
      ),
    );

    setAllocateProgress(100);
    setAllocating(false);

    const failures = results
      .map((r, i) => (r.status === 'rejected' ? `${sns[i]}: ${r.reason instanceof Error ? r.reason.message : 'error'}` : null))
      .filter(Boolean) as string[];

    if (failures.length > 0) {
      setAllocateError(`${failures.length} device(s) failed:\n${failures.join('\n')}`);
    } else {
      onSuccess(`${sns.length} device(s) allocated to order ${order.orderReference}.`);
      onClose();
    }
  };

  const handleClose = () => {
    if (allocating) return;
    setSearchTerm('');
    setFilters({});
    setSelectedIds([]);
    setAllocateError(null);
    setPage(1);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Add Devices to Order — {order.orderReference}</DialogTitle>
      <DialogContent dividers sx={{ pb: 1 }}>
        {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
        {allocateError && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
            {allocateError}
          </Alert>
        )}

        {/* Site selection removed per UX request */}

        {/* Device picker */}
        <EnhancedTable<OpsDeviceDetail>
          data={pagedRows}
          totalCount={filtered.length}
          loading={loadingDevices}
          columns={tableColumns}
          getRowId={(row) => row.serialNumber}
          page={page}
          pageSize={pageSize}
          pageSizeOptions={[10, 25, 50]}
          onPageChange={setPage}
          onPageSizeChange={(ps) => { setPageSize(ps); setPage(1); }}
          searchable
          searchTerm={searchTerm}
          searchPlaceholder="Search serial / model / batch…"
          onSearchChange={(t) => { setSearchTerm(t); setPage(1); }}
          filterOptions={[
            {
              field: 'deviceCategory',
              label: 'Device Type',
              type: 'select',
              options: [
                { value: '', label: 'All types' },
                ...CATEGORY_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
              ],
            },
          ]}
          filters={filters}
          onFilterChange={(f) => { setFilters(f as Record<string, string>); setPage(1); }}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          maxHeight={340}
          stickyHeader
          emptyStateTitle="No in-stock devices"
          emptyStateDescription="All matching devices are already allocated or no devices exist."
        />

        {allocating && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress variant="determinate" value={allocateProgress} />
            <Typography variant="caption" color="text.secondary">
              Allocating… {allocateProgress}%
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <AppButton onClick={handleClose} disabled={allocating}>
          Cancel
        </AppButton>
        <AppButton
          variant="contained"
          onClick={() => void handleAllocate()}
          disabled={allocating || selectedIds.length === 0}
          startIcon={allocating ? <CircularProgress size={14} color="inherit" /> : <AddDevicesIcon />}
        >
          Allocate {selectedIds.length > 0 ? `${selectedIds.length} Device(s)` : 'Selected'}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function OrderDetail() {
  const theme = useTheme();
  const { user } = useAuth();
  const orderReference = decodeURIComponent(window.location.pathname.split('/').pop() ?? '');

  const [order, setOrder] = useState<Order | null>(null);
  const [devices, setDevices] = useState<OpsDeviceDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [availability, setAvailability] = useState<OrderInventoryAvailability | null>(null);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState<Partial<UpdateOrderRequest>>({});

  // Add devices dialog (supports deep-link: ?addDevices=1)
  const [addDevicesOpen, setAddDevicesOpen] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('addDevices') === '1';
  });

  const [returnModalDevice, setReturnModalDevice] = useState<{
    serialNumber: string;
    inventoryStatus: DeviceInventoryStatus;
  } | null>(null);

  const [claimOnBehalfOpen, setClaimOnBehalfOpen] = useState(false);

  // Fleet table page state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const navigateTo = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleCopyClaimCode = useCallback(async () => {
    if (!order?.claimCode) return;
    await navigator.clipboard.writeText(order.claimCode);
    setSuccessMsg('Shipment claim code copied.');
  }, [order]);

  const handlePrintClaimCode = useCallback(() => {
    if (!order?.claimCode) return;
    const popup = window.open('', '_blank', 'width=420,height=520');
    if (!popup) return;
    popup.document.write(`
      <html>
        <head><title>Shipment Claim Code</title></head>
        <body style="font-family: Arial, sans-serif; padding: 24px;">
          <h2 style="margin: 0 0 12px;">Shipment Claim Code</h2>
          <p style="margin: 0 0 8px;">Order: ${order.orderReference}</p>
          <p style="margin: 0 0 24px;">Tenant: ${order.tenantName ?? order.tenantId}</p>
          <div style="border: 2px solid ${theme.palette.text.primary}; border-radius: 8px; padding: 24px; text-align: center; font-size: 32px; letter-spacing: 8px; font-weight: 700;">
            ${order.claimCode}
          </div>
        </body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    popup.print();
  }, [order, theme.palette.text.primary]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await providerOrdersService.getOrder(orderReference);
      setOrder(res.data.order);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [orderReference]);

  const loadDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const res = await providerOrdersService.getOrderDevices(orderReference);
      setDevices(res.data.devices);
    } catch {
      // non-fatal
    } finally {
      setDevicesLoading(false);
    }
  }, [orderReference]);

  const loadAvailability = useCallback(async () => {
    try {
      const res = await providerOrdersService.getOrderInventoryAvailability(orderReference);
      setAvailability(res.data.availability);
    } catch {
      // non-fatal
      setAvailability(null);
    }
  }, [orderReference]);

  useEffect(() => {
    void load();
    void loadDevices();
    void loadAvailability();
  }, [load, loadDevices, loadAvailability]);

  useEffect(() => {
    if (!addDevicesOpen) return;
    const current = new URL(window.location.href);
    if (!current.searchParams.has('addDevices')) return;
    current.searchParams.delete('addDevices');
    const next = `${current.pathname}${current.search ? `?${current.searchParams.toString()}` : ''}`;
    window.history.replaceState({}, '', next);
  }, [addDevicesOpen]);

  const refreshAll = useCallback(async () => {
    await Promise.all([load(), loadDevices(), loadAvailability()]);
  }, [load, loadDevices, loadAvailability]);

  const handleOpenReturn = useCallback((device: OpsDeviceDetail) => {
    setReturnModalDevice({
      serialNumber: device.serialNumber,
      inventoryStatus: (device.inventoryStatus ?? 'active') as DeviceInventoryStatus,
    });
  }, []);

  const handleReturnSuccess = useCallback(() => {
    setSuccessMsg('Device marked as returned.');
    setReturnModalDevice(null);
    void refreshAll();
  }, [refreshAll]);

  const handleConfirmOrder = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await providerOrdersService.confirmOrder(orderReference);
      setOrder(res.data.order);
      setSuccessMsg(`Order confirmed. ${res.data.devicesChecked} device(s) validated.`);
      await refreshAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to confirm order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelAndRelease = async () => {
    const reason = window.prompt('Cancellation reason (required):', 'Customer requested cancellation before shipment');
    if (!reason) return;

    setActionLoading(true);
    setError(null);
    try {
      const res = await providerOrdersService.cancelOrder(orderReference, { reason });
      setOrder(res.data.order);
      setSuccessMsg(`Order cancelled. ${res.data.devicesReleased} device(s) released to in-stock.`);
      await refreshAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleShipAndLock = async () => {
    if (!window.confirm('Ship this order and lock all allocated devices as shipped?')) {
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      const res = await providerOrdersService.shipOrder(orderReference);
      setOrder(res.data.order);
      setSuccessMsg(`Order shipped. ${res.data.devicesShipped} device(s) transitioned to shipped.`);
      await refreshAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to ship order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditOpen = () => {
    if (!order || order.status === 'completed') return;
    setEditForm({
      status:                 order.status,
      externalReference:      order.externalReference ?? undefined,
      expectedShipDate:       order.expectedShipDate ?? undefined,
      actualShipDate:         order.actualShipDate ?? undefined,
      notes:                  order.notes ?? undefined,
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: UpdateOrderRequest = {};
      if (editForm.status !== undefined)                payload.status = editForm.status;
      if (editForm.externalReference !== undefined)     payload.externalReference = editForm.externalReference || null;
      if (editForm.expectedShipDate !== undefined)      payload.expectedShipDate = editForm.expectedShipDate || null;
      if (editForm.actualShipDate !== undefined)        payload.actualShipDate = editForm.actualShipDate || null;
      if (editForm.notes !== undefined)                 payload.notes = editForm.notes || null;

      const res = await providerOrdersService.updateOrder(orderReference, payload);
      setOrder(res.data.order);
      setEditOpen(false);
      setSuccessMsg('Order updated successfully.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const pagedDevices = devices.slice((page - 1) * pageSize, page * pageSize);
  const deviceColumns = useMemo(
    () => buildDeviceColumns(navigateTo, { onReturn: handleOpenReturn }),
    [handleOpenReturn],
  );
  const returnableDeviceCount = devices.filter((d) => isReturnableDeviceStatus(d.inventoryStatus)).length;
  const showReturnIntakeHint =
    Boolean(order && (order.status === 'shipped' || order.status === 'completed') && returnableDeviceCount > 0);
  const canClaimOnBehalfRole = Boolean(
    user?.role && (PROVIDER_ADMIN_DEPLOYMENT_ROLES as readonly string[]).includes(user.role),
  );
  const showClaimOnBehalf = Boolean(canClaimOnBehalfRole && availability?.canClaimOnBehalf);
  const deviceSummaryByCategory = Array.from(
    devices.reduce((acc, device) => {
      const key = device.deviceCategory ?? 'unknown';
      const existing = acc.get(key) ?? { category: key, linked: 0, allocated: 0, shipped: 0 };
      existing.linked += 1;
      const state = device.inventoryStatus ?? 'in-stock';
      if (state === 'allocated') existing.allocated += 1;
      if (state === 'shipped') existing.shipped += 1;
      acc.set(key, existing);
      return acc;
    }, new Map<string, { category: string; linked: number; allocated: number; shipped: number }>()).values(),
  ).sort((a, b) => a.category.localeCompare(b.category));

  // ── Loading / error states ─────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !order) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!order) return null;

  const canEditOrderDetails = order.status !== 'completed';

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>

      {/* ── Header — same pattern as DeviceDetail ── */}
      <Box display="flex" alignItems="center" mb={2}>
        <Tooltip title="Back to Orders">
          <AppIconButton onClick={() => navigateTo('/provider/orders')} size="small">
            <BackIcon />
          </AppIconButton>
        </Tooltip>
        <Typography variant="h5" fontWeight="bold" fontFamily="monospace" sx={{ ml: 1 }}>
          {order.orderReference}
        </Typography>
        <OrderStatusChip status={order.status} sx={{ ml: 1 }} />
        <Box flexGrow={1} />
        {canEditOrderDetails ? (
          <AppButton
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={handleEditOpen}
            sx={{ mr: 1 }}
          >
            Edit Order
          </AppButton>
        ) : (
          <Tooltip title="Completed orders cannot be edited.">
            <span>
              <AppButton
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                disabled
                sx={{ mr: 1 }}
              >
                Edit Order
              </AppButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Refresh">
          <AppIconButton onClick={() => void refreshAll()} disabled={loading || actionLoading}>
            <RefreshIcon />
          </AppIconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {/* ── Full-width layout ── */}
      <Grid container spacing={3}>

        <Grid item xs={12}>

          {/* Order Info */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Order Info</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <DetailRow label="Order Reference" value={order.orderReference} />
            <DetailRow label="External Reference" value={order.externalReference} />
            <DetailRow label="Tenant" value={order.tenantName ?? order.tenantId} />
            <DetailRow label="Order Date" value={formatDate(order.orderDate)} />
            <DetailRow label="Status" value={<OrderStatusChip status={order.status} />} />
          </Paper>

          {(order.lineItems?.length ?? 0) > 0 && (
            <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">Requested Line Items (PO)</Typography>
                <AppButton
                  size="small"
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={() => {
                    const text = (order.lineItems ?? [])
                      .map((item) => formatOrderLineItemSummary(item))
                      .join('\n');
                    void navigator.clipboard.writeText(text);
                    setSuccessMsg('PO line items copied to clipboard.');
                  }}
                >
                  Copy PO Lines
                </AppButton>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Box
                display="grid"
                gridTemplateColumns="120px minmax(160px, 1fr) 80px"
                gap={1}
                sx={{ mb: 0.5, px: 0.5 }}
              >
                <Typography variant="caption" fontWeight={700}>SKU</Typography>
                <Typography variant="caption" fontWeight={700}>Product</Typography>
                <Typography variant="caption" fontWeight={700}>Qty</Typography>
              </Box>
              <Stack spacing={0.5}>
                {order.lineItems!.map((item) => (
                  <Box
                    key={`${item.sku}-${item.deviceCategory}`}
                    display="grid"
                    gridTemplateColumns="120px minmax(160px, 1fr) 80px"
                    gap={1}
                  >
                    <Typography variant="body2" fontFamily="monospace">{item.sku}</Typography>
                    <Typography variant="body2">{item.productName}</Typography>
                    <Typography variant="body2" fontFamily="monospace">{item.quantity}</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Logistics */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Logistics</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <DetailRow label="Expected Ship Date" value={formatDate(order.expectedShipDate)} />
            <DetailRow label="Actual Ship Date" value={formatDate(order.actualShipDate)} />
          </Paper>

          {/* Inventory Availability */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Inventory Availability</Typography>
            <Divider sx={{ mb: 1.5 }} />
            {availability ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Devices Summary
                </Typography>
                <Box
                  display="grid"
                  gridTemplateColumns="minmax(150px, 1fr) 90px 90px 90px"
                  gap={1}
                  sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                >
                  <Typography variant="caption" fontWeight={700}>Category</Typography>
                  <Typography variant="caption" fontWeight={700}>Linked</Typography>
                  <Typography variant="caption" fontWeight={700}>Allocated</Typography>
                  <Typography variant="caption" fontWeight={700}>Shipped</Typography>
                </Box>
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  {deviceSummaryByCategory.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No linked devices yet.</Typography>
                  ) : (
                    deviceSummaryByCategory.map((row) => (
                      <Box
                        key={row.category}
                        display="grid"
                        gridTemplateColumns="minmax(150px, 1fr) 90px 90px 90px"
                        gap={1}
                      >
                        <Typography variant="body2">{getCategoryLabel(row.category)}</Typography>
                        <Typography variant="body2" fontFamily="monospace">{row.linked}</Typography>
                        <Typography variant="body2" fontFamily="monospace">{row.allocated}</Typography>
                        <Typography variant="body2" fontFamily="monospace">{row.shipped}</Typography>
                      </Box>
                    ))
                  )}
                </Stack>

                <DetailRow label="Linked Non-IoT Devices" value={availability.totalDevices} />
                <Box display="flex" flexWrap="wrap" gap={1} mt={1.5}>
                  {availability.byStatus
                    .filter((row) => row.count > 0)
                    .map((row) => (
                      <DeviceInventoryStatusChip
                        key={row.status}
                        status={row.status}
                        label={`${getDeviceInventoryStatusLabel(row.status)}: ${row.count}`}
                      />
                    ))}
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                  <AppButton
                    variant="contained"
                    color="info"
                    startIcon={<ConfirmIcon />}
                    disabled={actionLoading || !availability.canConfirm || order.status === 'confirmed'}
                    onClick={() => void handleConfirmOrder()}
                  >
                    Confirm Order
                  </AppButton>
                  <AppButton
                    variant="outlined"
                    color="error"
                    startIcon={<StopIcon />}
                    disabled={actionLoading || !availability.canCancel}
                    onClick={() => void handleCancelAndRelease()}
                  >
                    Cancel and Release
                  </AppButton>
                  <AppButton
                    variant="contained"
                    color="warning"
                    startIcon={<ShipIcon />}
                    disabled={actionLoading || !availability.canShip}
                    onClick={() => void handleShipAndLock()}
                  >
                    Ship and Lock
                  </AppButton>
                </Stack>

                <Alert severity="info" sx={{ mt: 2 }}>
                  Guardrails: Cancel is allowed only for Draft/Confirmed orders. Ship transitions allocated devices to shipped.
                </Alert>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Availability summary is temporarily unavailable.
              </Typography>
            )}
          </Paper>

          {/* Shipment Claim Code */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1}>
              <Typography variant="subtitle1" fontWeight="bold">Shipment Claim Code</Typography>
              {order.claimCode && (
                <Stack direction="row" spacing={1}>
                  {showClaimOnBehalf && (
                    <AppButton
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<ClaimOnBehalfIcon />}
                      onClick={() => setClaimOnBehalfOpen(true)}
                      disabled={actionLoading}
                    >
                      Claim for Tenant
                    </AppButton>
                  )}
                  <AppButton size="small" startIcon={<CopyIcon />} onClick={() => void handleCopyClaimCode()}>
                    Copy
                  </AppButton>
                  <AppButton size="small" startIcon={<PrintIcon />} onClick={handlePrintClaimCode}>
                    Print Label
                  </AppButton>
                </Stack>
              )}
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            {order.claimCode ? (
              <>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    display: 'inline-flex',
                    mb: 1.5,
                  }}
                >
                  <Typography variant="h5" fontFamily="monospace" fontWeight="bold" sx={{ letterSpacing: '0.22em' }}>
                    {order.claimCode}
                  </Typography>
                </Box>
                <DetailRow label="Available When" value="Order marked shipped" />
                <DetailRow label="Claimed On" value={formatDate(order.claimedAt)} />
                <DetailRow
                  label="Claimed By"
                  value={
                    order.claimedByName
                    ?? (order.claimedByUserId && order.claimedByUserId !== 'unknown'
                      ? order.claimedByUserId
                      : null)
                    ?? '—'
                  }
                />
                {order.claimedAt && (
                  <Alert severity="success" sx={{ mt: 1.5 }}>
                    Shipment claimed on {formatDate(order.claimedAt)}.
                  </Alert>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Claim code will be generated automatically when this order moves to Shipped.
              </Typography>
            )}
            {showClaimOnBehalf && !order.claimCode && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                This shipped order is ready for provider claim on behalf of the tenant.
              </Alert>
            )}
          </Paper>

          {/* Notes */}
          {order.notes && (
            <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Notes</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{order.notes}</Typography>
            </Paper>
          )}

          {/* Device Fleet — EnhancedTable */}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                Device Fleet ({devices.length})
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <AppButton
                  size="small"
                  variant="outlined"
                  startIcon={<AddDevicesIcon />}
                  onClick={() => setAddDevicesOpen(true)}
                  disabled={order.status === 'shipped' || order.status === 'completed' || order.status === 'cancelled'}
                >
                  Add Devices
                </AppButton>
                <Tooltip title="Refresh devices">
                  <AppIconButton size="small" onClick={loadDevices} disabled={devicesLoading}>
                    {devicesLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                  </AppIconButton>
                </Tooltip>
              </Stack>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {showReturnIntakeHint && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {returnableDeviceCount} device(s) on this order can be returned (shipped, claimed, or active).
                Use <strong>Return</strong> in the table below when hardware is back in the warehouse.
              </Alert>
            )}
            {devices.length === 0 && !devicesLoading ? (
              <Box display="flex" flexDirection="column" alignItems="center" py={6} sx={{ opacity: 0.4 }}>
                <ShippingIcon sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="body2">No devices linked to this order yet.</Typography>
              </Box>
            ) : (
              <EnhancedTable<OpsDeviceDetail>
                data={pagedDevices}
                totalCount={devices.length}
                columns={deviceColumns}
                loading={devicesLoading}
                page={page}
                pageSize={pageSize}
                pageSizeOptions={[10, 25, 50]}
                onPageChange={setPage}
                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                getRowId={(r) => r.serialNumber}
                onRowClick={(row) => navigateTo(`/provider/devices/${row.serialNumber}`)}
                emptyStateTitle="No devices"
                emptyStateDescription="No devices have been linked to this order."
                maxHeight="none"
              />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Order — {order.orderReference}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <AppTextField
              label="Status"
              select
              fullWidth
              size="small"
              value={editForm.status ?? order.status}
              onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value as OrderStatus }))}
              SelectProps={{
                renderValue: (value) => <OrderStatusChip status={value as OrderStatus} />,
              }}
            >
              {ORDER_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  <OrderStatusChip status={s} />
                </MenuItem>
              ))}
            </AppTextField>

            <AppTextField
              label="External Reference (PO / CRM)"
              fullWidth
              size="small"
              value={editForm.externalReference ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, externalReference: e.target.value }))}
            />

            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.5 }}>
                Logistics
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Box>

            <Box display="flex" gap={2}>
              <AppTextField
                label="Expected Ship"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={editForm.expectedShipDate ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, expectedShipDate: e.target.value }))}
              />
              <AppTextField
                label="Actual Ship"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={editForm.actualShipDate ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, actualShipDate: e.target.value }))}
              />
            </Box>

            <AppTextField
              label="Notes"
              fullWidth
              size="small"
              multiline
              rows={3}
              value={editForm.notes ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setEditOpen(false)} disabled={saving} startIcon={<CancelIcon />}>
            Cancel
          </AppButton>
          <AppButton
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          >
            Save Changes
          </AppButton>
        </DialogActions>
      </Dialog>

      {/* ── Add Devices Dialog ── */}
      {order && (
        <AddDevicesDialog
          open={addDevicesOpen}
          order={order}
          onClose={() => setAddDevicesOpen(false)}
          onSuccess={(msg) => {
            setSuccessMsg(msg);
            void refreshAll();
          }}
        />
      )}

      {returnModalDevice && (
        <ReturnDeviceModal
          open
          serialNumber={returnModalDevice.serialNumber}
          inventoryStatus={returnModalDevice.inventoryStatus}
          onClose={() => setReturnModalDevice(null)}
          onSuccess={handleReturnSuccess}
        />
      )}

      {order && (
        <ProviderClaimOnBehalfModal
          open={claimOnBehalfOpen}
          orderReference={order.orderReference}
          tenantName={order.tenantName}
          onClose={() => setClaimOnBehalfOpen(false)}
          onSuccess={(devicesClaimed) => {
            setSuccessMsg(`Shipment claimed for tenant. ${devicesClaimed} device(s) updated.`);
            setClaimOnBehalfOpen(false);
            void refreshAll();
          }}
        />
      )}
    </Container>
  );
}
